<script lang="ts">
    import ManageAssignmentsPage from '@Alternatives/Assignments/Pages/ManageAssignmentsPage'
    export default ManageAssignmentsPage
</script>

<template>
    <div>
        <UsersAssignerDialog :viewModel="usersAssignerDialog" 
                             @assign-users="handleBulkAssignUsersCommand" />

        <PortfolioOrDecisionNavigation :workspaceId="workspaceId" :projectId="tchProjectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Assign {{rm?.AlternativesLabel}}</h1>
                </div>
            </div>

            <hr style="border-color: #bbb; margin: 20px 0 60px;" />

            <div v-if="rm" class="d-flex align-items-center gap-3 flex-wrap mb-3">
                <!-- List Filter Dropdown -->
                <div class="dropdown">
                    <button class="btn"
                            :class="filterByListId ? 'btn-info' : 'btn-outline-secondary'"
                            type="button"
                            data-toggle="dropdown">
                        <i class="fas fa-filter mr-1"
                           :class="filterByListId ? 'text-warning' : 'text-muted'">
                        </i>
                        <span>List</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable">
                        <a class="dropdown-item"
                           :class="{ 'active': filterByListId === null }"
                           @click="setFilterByList(null)">
                            Any / No filter
                        </a>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header">Filter by list</h6>
                        <a class="dropdown-item"
                           v-for="l in rm.Lists"
                           :key="l.Id"
                           :class="{ 'active': filterByListId === l.Id }"
                           @click="setFilterByList(l.Id)">
                            {{ l.Name }}
                        </a>
                    </div>
                </div>

                <!-- Contributor Filter Dropdown -->
                <div class="dropdown ml-3">
                    <button class="btn"
                            :class="filterByUserId !== null ? 'btn-info' : 'btn-outline-secondary'"
                            type="button"
                            data-toggle="dropdown">
                        <i class="fas fa-filter mr-1"
                           :class="filterByUserId !== null ? 'text-warning' : 'text-muted'">
                        </i>
                        <span>Contributor</span>
                    </button>
                    <div class="dropdown-menu dropdown-menu-right dropdown-menu-scrollable">
                        <!-- Reset filter (show all) -->
                        <a class="dropdown-item"
                           :class="{ 'active': filterByUserId === null }"
                           @click="setFilterByUser(null)">
                            Any / No filter
                        </a>
                        <div class="dropdown-divider"></div>
                        <h6 class="dropdown-header">Filter by user</h6>
                        <!-- Regular users -->
                        <a class="dropdown-item"
                           v-for="user in users"
                           :key="user.Id"
                           :class="{ 'active': filterByUserId === user.Id }"
                           @click="setFilterByUser(user.Id)">
                            {{ user.Name }}
                        </a>

                        <div class="dropdown-divider"></div>

                        <!-- Unassigned (no contributors) -->
                        <a class="dropdown-item"
                           :class="{ 'active': filterByUserId === -1 }"
                           @click="setFilterByUser(-1)">
                            Unassigned
                        </a>
                    </div>
                </div>

            </div>

            <div v-if="rm">
                <div class="toolbar mt-5 d-flex justify-content-between align-items-center">
                    <!-- Search Input (Left) -->
                    <div class="search-container">
                        <input type="text"
                               v-model="searchPhrase"
                               class="form-control form-control-sm"
                               placeholder="Search..." />
                    </div>

                    <!-- Assign Selected Button (Right) -->
                    <button type="button"
                            class="btn btn-sm btn-primary d-flex align-items-center"
                            :disabled="noneSelected"
                            @click="showBulkUsersAssignerDialog">
                        <i class="fas fa-users-cog mr-2"></i> Assign ({{ selected.length }})
                    </button>
                </div>


                <table class="table table-bordered table-hover">
                    <thead>
                        <tr>
                            <th style="width: 40px; min-width: 40px !important;">
                                <div class="btn-group">
                                    <a class="pointer" v-if="noneSelected" @click="selectAll"><i class="fa-regular fa-square"></i></a>
                                    <a class="pointer" v-if="anySelected && !allSelected" @click="unselectAll"><i class="fa fa-minus-square"></i></a>
                                    <a class="pointer" v-if="anySelected && allSelected" @click="unselectAll"><i class="fa-solid fa-square-check"></i></a>
                                </div>
                            </th>
                            <th>{{rm.AlternativeLabel}}</th>
                            <th>Contributor(s)</th>
                            <th style="width: 120px;"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in alternativesView" :key="p.Id">
                            <td><input type="checkbox" v-model="p.IsUiSelected" /></td>
                            <td>{{ p.Name }}</td>
                            <td>
                                <div class="d-flex flex-wrap">
                                    <div class="dropdown d-inline-block position-relative mr-2 mb-1" v-for="uId in p.UserIds" :key="uId">
                                        <button class="btn btn-sm badge dropdown-toggle px-2 py-1 user-badge"
                                                :class="filterByUserId === uId ? 'badge-info' : 'badge-secondary'"
                                                type="button"
                                                data-toggle="dropdown"
                                                aria-expanded="false">
                                            {{ getUserName(uId) }}
                                        </button>
                                        <div class="dropdown-menu">
                                            <!-- Filter option -->
                                            <a class="dropdown-item"
                                               @click="setFilterByUser(uId)">
                                                <i class="fas fa-filter mr-2"></i> Filter by user
                                            </a>
                                            <!-- Unassign option -->
                                            <a class="dropdown-item text-danger"
                                               @click="handleUnassignUserCommand(p, uId)">
                                                <i class="fas fa-user-slash mr-2"></i> Unassign
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td>
                                <button type="button"
                                        class="btn btn-sm btn-outline-primary d-flex align-items-center"
                                        @click="showUsersAssignerDialog(p)">
                                    <i class="fas fa-user-plus mr-1"></i> Assign
                                </button>

                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>

    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>